import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getPurchasedServicesInventory,
  addInventory,
  updateInventory,
  deleteInventory,
} from "../../../../../../apis/Business/InventoryApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import { getInventoryServiceData } from "../../../../../../apis/Dropdown";
import { getTransportCountryData } from "../../../../../../apis/Dropdown";

const InventoryServiceTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownData, setDropdownData] = useState([]);
  const [unitService, setUnitService] = useState("");
  const [dropdownLocation, setDropdownLocation] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getPurchasedServicesInventory({
      businessId: businessId,
      setObjects: setData,
    });
    getInventoryServiceData({ setDropdownData: setDropdownData });
    getTransportCountryData({
      setDropdownData: setDropdownLocation,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateInventory({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        subcategory: "purchased_services",
        type_service: editedItem["type_service.value"]
          ? editedItem["type_service.value"].value
          : editedItem["type_service"]
          ? editedItem["type_service"].value
          : null,
        type_chemical: null,
        type_buildingcode: null,
        type_packaging: null,
        type_material: null,
        type_machinery: null,
        type_buildingmaterial: null,
        location: editedItem["location.value"]
          ? editedItem["location.value"].value
          : editedItem["location"]
          ? editedItem["location"].value
          : null,
        subcategoryId: editedItem["subcategory_obj"],
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addInventory({
        businessId,
        ...newItem,
        type_service: newItem["type_service.value"]
          ? newItem["type_service.value"].value
          : null,
        type_chemical: null,
        type_buildingcode: null,
        type_packaging: null,
        type_material: null,
        type_machinery: null,
        type_buildingmaterial: null,
        subcategory: "purchased_services",
        location: newItem["location.value"]
          ? newItem["location.value"].value
          : null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteInventory({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const dynamicPlaceholders = useMemo(
    () => ({
      "type_service.value": {
        getPlaceholder: (selectedValue, dropdownData) => {
          const selectedMaterial = dropdownData?.find(
            (option) =>
              option?.dropdown_name === selectedValue ||
              option?.dropdown_name_en === selectedValue
          );
          selectedMaterial && setUnitService(selectedMaterial.unit);
        },
        dropdownDataKey: "type_service.value",
      },
    }),
    []
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "nickname",
        header: t("INV_NICKNAME"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "80px",
      },
      {
        accessor: "location.value",
        header: t("PRODUCTION_COUNTRY"),
        type: "dropdown",
        dropdownData: dropdownLocation,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "type_service.value",
        header: t("TYPE_SERVICE"),
        type: "dropdown",
        dropdownData: dropdownData,
        disableSortBy: true,
        width: "110px",
        tipHeader: "Tip",
        tipBody: t("TIP_TYPE_SERVICE"),
        isCategory: true,
        optional: true,
      },
      {
        accessor: "qty",
        header: t("INV_QTY_SERVICES"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
      },
      {
        accessor: "manuel",
        header: "Manual kgCO₂/service",
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIP_MANUEL_SERVICE"),
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
        optional: unitService === "DKK" ? false : true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [dropdownData, t, unitService, dropdownLocation]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "type_service.value": dropdownData,
      "location.value": dropdownLocation,
    }),
    [dropdownData, dropdownLocation]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default InventoryServiceTable;
